<template>
  <div class="release-tickets-page">

    <!-- ── Release Modal ─────────────────────────────────── -->
    <b-modal
      id="release-ticket-modal"
      ref="releaseModal"
      centered
      no-close-on-backdrop
      no-close-on-esc
      size="md"
      hide-header
      hide-footer
      body-class="p-0"
    >
      <template #default>
        <div
          v-if="selectedSale"
          class="rt-modal"
        >
          <!-- Modal header band -->
          <div class="rt-modal__header">
            <div class="rt-modal__header-icon">
              <feather-icon
                icon="UnlockIcon"
                size="26"
              />
            </div>
            <div class="rt-modal__header-text">
              <h5 class="mb-0 text-white font-weight-bolder">
                Release Tags
              </h5>
              <small class="rt-modal__sub">
                Sale #{{ selectedSale.number }} &middot;
                {{ selectedSale.market.code ? selectedSale.market.code.toUpperCase() + ': ' : '' }}{{ selectedSale.market.name.toUpperCase() }}
              </small>
            </div>
            <button
              class="rt-modal__close"
              @click="releaseModal.hide()"
            >
              <feather-icon
                icon="XIcon"
                size="18"
              />
            </button>
          </div>

          <!-- Metric stat cards -->
          <div class="rt-modal__body">
            <b-row class="mb-75">
              <b-col cols="6">
                <div class="rt-stat">
                  <span class="rt-stat__label">Sale Number</span>
                  <span class="rt-stat__value rt-stat__value--brown">#{{ selectedSale.number }}</span>
                </div>
              </b-col>
              <b-col cols="6">
                <div class="rt-stat">
                  <span class="rt-stat__label">Status</span>
                  <span class="rt-stat__value">
                    <span
                      class="status-pill"
                      :class="selectedSale.isClosed ? 'status-pill--closed' : 'status-pill--open'"
                    >
                      <span class="status-dot" />
                      {{ selectedSale.isClosed ? 'Closed' : 'Open' }}
                    </span>
                  </span>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-75">
              <b-col cols="6">
                <div class="rt-stat">
                  <span class="rt-stat__label">Sale Date</span>
                  <span class="rt-stat__value">
                    {{ (selectedSale.effectiveDate || selectedSale.saleDate) | moment('DD MMM YYYY') }}
                  </span>
                </div>
              </b-col>
              <b-col cols="6">
                <div class="rt-stat">
                  <span class="rt-stat__label">Captured / Total Tickets</span>
                  <span class="rt-stat__value">
                    <span class="rt-stat__captured">{{ selectedSale.captured || 0 }}</span>
                    <span class="rt-stat__total"> / {{ selectedSale.ticketsCount || '—' }}</span>
                  </span>
                </div>
              </b-col>
            </b-row>

            <!-- Market row -->
            <div class="rt-market-row">
              <div class="rt-market-row__icon">
                <feather-icon
                  icon="MapPinIcon"
                  size="15"
                />
              </div>
              <div>
                <p class="mb-0 rt-market-row__label">
                  Warehouse / Market
                </p>
                <p class="mb-0 rt-market-row__value">
                  {{ selectedSale.market.code ? selectedSale.market.code.toUpperCase() + ': ' : '' }}{{ selectedSale.market.name.toUpperCase() }}
                </p>
              </div>
            </div>

            <!-- Warning notice -->
            <div class="rt-warning">
              <feather-icon
                icon="AlertTriangleIcon"
                size="15"
                class="flex-shrink-0 mr-75 rt-warning__icon"
              />
              <p class="mb-0 small">
                Releasing tickets will make them <strong>available for reuse</strong>
                in another sale. This action cannot be undone. Please review the details carefully.
              </p>
            </div>

            <!-- Footer buttons -->
            <div class="rt-modal__footer">
              <b-button
                variant="flat-secondary"
                size="sm"
                :disabled="releasing"
                @click="releaseModal.hide()"
              >
                Cancel
              </b-button>
              <b-button
                size="sm"
                class="release-confirm-btn"
                :disabled="releasing"
                @click="confirmRelease()"
              >
                <b-spinner
                  v-if="releasing"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="UnlockIcon"
                  size="13"
                  class="mr-50"
                />
                {{ releasing ? 'Processing...' : 'Release Ticket' }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
    <!-- ── /Release Modal ─────────────────────────────────── -->

    <!-- ── Location Filter Card ──────────────────────────── -->
    <b-card
      no-body
      class="filter-card mb-1"
    >
      <div class="filter-card__header">
        <feather-icon
          icon="SlidersIcon"
          size="14"
          class="mr-50"
        />
        <span class="filter-card__title">Select Location</span>
      </div>

      <b-card-body class="pt-1 pb-75">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="8"
          >
            <location-filters
              size="sm"
              align="horizontal"
              @filter="onLocationFilter"
            />
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mt-1 mt-md-0"
          >
            <div class="filter-select-wrap">
              <label class="filter-label">
                <span class="step-diamond" />
                Warehouse / Market
              </label>
              <b-form-select
                v-model="selectedMarketId"
                size="sm"
                :disabled="!markets.length"
                class="filter-select"
                @change="onMarketChange"
              >
                <b-form-select-option :value="null">
                  {{ locationFilter ? (markets.length ? 'Select Market...' : 'No markets found') : 'Select area first...' }}
                </b-form-select-option>
                <b-form-select-option
                  v-for="market in markets"
                  :key="market.id"
                  :value="market.id"
                >
                  {{ market.code ? market.code.toUpperCase() + ': ' : '' }}{{ market.name.toUpperCase() }}
                </b-form-select-option>
              </b-form-select>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
    <!-- ── /Location Filter Card ──────────────────────────── -->

    <!-- ── Sales List Card ───────────────────────────────── -->
    <b-card
      no-body
      class="mb-0 sales-card"
    >
      <!-- Card header -->
      <div class="sales-card__header">
        <div class="d-flex align-items-center">
          <div class="sales-header-icon">
            <feather-icon
              icon="TagIcon"
              size="16"
            />
          </div>
          <div class="ml-1">
            <h6 class="mb-0 font-weight-bolder sales-title">
              Sales
              <span
                v-if="selectedMarketId && totalRecords > 0"
                class="record-badge ml-25"
              >{{ totalRecords }}</span>
            </h6>
            <small class="sales-subtitle d-none d-sm-block">
              {{ selectedMarketId ? 'Select a sale and click Release to proceed' : 'Select a market above to load sales' }}
            </small>
          </div>
        </div>

        <div
          v-if="selectedMarketId"
          class="sales-card__controls"
        >
          <b-form-input
            v-model="searchQuery"
            placeholder="Search..."
            size="sm"
            class="sales-search"
          />
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector select-size-sm sales-perpage"
          />
        </div>
      </div>

      <!-- ── Empty State ─────────────── -->
      <div
        v-if="!selectedMarketId"
        class="empty-state"
      >
        <div class="empty-state__icon">
          <div class="empty-state__ring" />
          <feather-icon
            icon="Layers"
            size="38"
            class="empty-state__ico"
          />
        </div>
        <h5 class="mt-2 mb-25 font-weight-bolder empty-state__title">
          No Market Selected
        </h5>
        <p class="empty-state__text mb-2">
          Use the filters above to locate a <strong>Region</strong>, "<strong>Area</strong>", and <strong>Market</strong>
          — then choose a sale to release its tickets for reuse.
        </p>
        <div class="empty-state__steps">
          <div class="empty-step">
            <span class="empty-step__num">1</span>
            <span>Region</span>
          </div>
          <div class="empty-step__arrow">
            <feather-icon
              icon="ArrowRightIcon"
              size="12"
            />
          </div>
          <div class="empty-step">
            <span class="empty-step__num">2</span>
            <span>Area</span>
          </div>
          <div class="empty-step__arrow">
            <feather-icon
              icon="ArrowRightIcon"
              size="12"
            />
          </div>
          <div class="empty-step">
            <span class="empty-step__num empty-step__num--gold">3</span>
            <span>Market</span>
          </div>
        </div>
      </div>
      <!-- ── /Empty State ─────────────── -->

      <!-- ── Sales Table ─────────────── -->
      <b-table
        v-if="selectedMarketId"
        ref="records"
        striped
        hover
        small
        responsive
        class="mb-0 sales-table"
        :items="fetch"
        :fields="columns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No sales found for the selected market."
        :sort-desc.sync="isSortDirDesc"
      >
        <template #cell(market)="data">
          <div class="market-cell">
            <span class="market-cell__code">{{ data.item.market.code ? data.item.market.code.toUpperCase() : '—' }}</span>
            <small class="market-cell__name">{{ data.item.market.name.toUpperCase() }}</small>
          </div>
        </template>

        <template #cell(number)="data">
          <span class="sale-number">#{{ data.item.number }}</span>
        </template>

        <template #cell(saleDate)="data">
          <span class="text-nowrap small">
            {{ (data.item.effectiveDate || data.item.saleDate) | moment('DD MMM YYYY') }}
          </span>
        </template>

        <template #cell(isClosed)="data">
          <span
            class="status-pill"
            :class="data.item.isClosed ? 'status-pill--closed' : 'status-pill--open'"
          >
            <span class="status-dot" />
            {{ data.item.isClosed ? 'Closed' : 'Open' }}
          </span>
        </template>

        <template #cell(ticketsCount)="data">
          <div
            v-if="data.item.ticketsCount > 0"
            class="tickets-cell"
          >
            <span class="small">
              <span class="tickets-captured">{{ data.item.captured }}</span>
              <span class="tickets-total"> / {{ data.item.ticketsCount }}</span>
            </span>
            <b-progress
              :max="data.item.ticketsCount"
              :value="data.item.captured"
              height="3px"
              class="mt-25 tickets-cell__bar"
            />
          </div>
          <span
            v-else
            class="text-muted"
          >—</span>
        </template>

        <template #cell(actions)="data">
          <b-button
            size="sm"
            class="action-btn"
            :disabled="releasing"
            @click="invokeRelease(data.item)"
          >
            <feather-icon
              icon="UnlockIcon"
              size="12"
              class="mr-25"
            />
            Release
          </b-button>
        </template>
      </b-table>
      <!-- ── /Sales Table ─────────────── -->

      <!-- Table footer -->
      <div
        v-if="selectedMarketId"
        class="sales-card__footer"
      >
        <span class="footer-info">
          Showing {{ from }} to {{ to }} of {{ totalRecords }} entries
        </span>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="perPage"
          first-number
          last-number
          size="sm"
          class="mb-0 tobacco-pagination"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="16"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="16"
            />
          </template>
        </b-pagination>
      </div>

    </b-card>
    <!-- ── /Sales List Card ───────────────────────────────── -->

  </div>
</template>

<script>
import {
    BRow, BCol, BCard, BCardBody, BTable, BPagination,
    BButton, BFormSelect, BFormSelectOption, BFormInput,
    BSpinner, BModal, BProgress,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { ref, onUnmounted, watch } from '@vue/composition-api'
import salesStoreModule from '@/views/cromis/sales/sale/salesStoreModule'
import LocationFilters from '@core/components/cromis/filters/LocationFilters'

export default {
    components: {
        BRow, BCol, BCard, BCardBody, BTable, BPagination,
        BButton, BFormSelect, BFormSelectOption, BFormInput,
        BSpinner, BModal, BProgress,
        vSelect,
        LocationFilters,
    },
    setup(props, context) {
        const CROMIS_STORE_MODULE_NAME = 'cromis-sale'

        if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) store.registerModule(CROMIS_STORE_MODULE_NAME, salesStoreModule)
        onUnmounted(() => { if (store.hasModule(CROMIS_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_STORE_MODULE_NAME) })

        const releaseModal = ref(null)
        const records = ref(null)
        const releasing = ref(false)
        const selectedSale = ref(null)
        const selectedMarketId = ref(null)
        const markets = ref([])
        const locationFilter = ref(null)

        const from = ref(0)
        const to = ref(0)
        const totalRecords = ref(0)
        const currentPage = ref(1)
        const perPage = ref(10)
        const perPageOptions = [5, 10, 25, 50]
        const searchQuery = ref('')
        const sortBy = ref('number')
        const isSortDirDesc = ref(true)

        const columns = [
            { key: 'market', label: 'Market / Warehouse', sortable: false },
            { key: 'number', label: 'Sale #', sortable: true },
            { key: 'saleDate', label: 'Sale Date', sortable: true, thClass: 'd-none d-sm-table-cell', tdClass: 'd-none d-sm-table-cell' },
            { key: 'isClosed', label: 'Status', sortable: false, thClass: 'd-none d-sm-table-cell', tdClass: 'd-none d-sm-table-cell' },
            { key: 'ticketsCount', label: 'Captured / Total', sortable: false, thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
            { key: 'actions', label: '', sortable: false, tdClass: 'text-right col-action' },
        ]

        const refetch = () => { if (records.value) records.value.refresh() }

        watch([currentPage, perPage, searchQuery, sortBy, isSortDirDesc], () => { refetch() })

        const fetch = (ctx, callback) => {
            if (!selectedMarketId.value) { callback([]); return }

            store.dispatch('cromis-sale/saleTags', {
                market_id: selectedMarketId.value,
                query: searchQuery.value,
                per_page: perPage.value,
                page: currentPage.value,
                sortBy: sortBy.value,
                sortDesc: isSortDirDesc.value,
            })
                .then(response => {
                    let { sales, meta } = response.data

                    sales = sales.map(sale => {
                      // Flatten all tags from all societies
                      const tags = sale.societies.flatMap(society => society.tags || [])

                      // Total tags count
                      const tagsCount = tags.length

                      // Tags where ticket is not null
                      const capturedCount = tags.filter(tag => tag.ticket).length

                      return {
                        ...sale,
                        captured: capturedCount,
                        ticketsCount: tagsCount,
                      }
                    })

                    console.log('Fetched sales with tags:', sales)

                    callback(sales)
                    totalRecords.value = meta.total
                    from.value = meta.from
                    to.value = meta.to
                })
                .catch(error => {
                  console.error('Error fetching sales with tags:', error)
                  callback([])
                })
        }

        const onLocationFilter = async location => {
            selectedMarketId.value = null
            markets.value = []
            locationFilter.value = location
            if (!location) return

            await store.dispatch('cromis-sale/markets', { location_id: location.id })
                .then(response => { markets.value = response.data.markets })
                .catch(() => {})
        }

        const onMarketChange = () => {
            currentPage.value = 1
            refetch()
        }

        const invokeRelease = item => {
            selectedSale.value = item
            releaseModal.value.show()
        }

        const confirmRelease = () => {
            console.log('Initiating release for sale:', selectedSale.value)
            const saleNumber = selectedSale.value.number

            context.root.$swal({
                title: 'Release Confirmation',
                html: `You are about to release tags from <b>${selectedSale.value.market.name} Sale #${saleNumber}</b>.<br>
                       <span style="font-size:0.85em;color:#8a7560;">
                         Tags will be made available for reuse and this cannot be undone.
                       </span>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, Release!',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
                customClass: {
                    confirmButton: 'btn btn-tobacco ml-1',
                    cancelButton: 'btn btn-outline-secondary',
                },
                buttonsStyling: false,
            }).then(result => {
                if (!result.isConfirmed) return

                releasing.value = true

                store.dispatch('cromis-sale/releaseTickets', { id: selectedSale.value.id })
                    .then(() => {
                        releasing.value = false
                        releaseModal.value.hide()
                        refetch()

                        context.root.$swal({
                            icon: 'success',
                            title: 'Tickets Released!',
                            text: `Sale #${saleNumber} tickets are now available for reuse.`,
                            timer: 3500,
                            showConfirmButton: false,
                        })
                    })
                    .catch(error => {
                        releasing.value = false
                        const message = (error.response && error.response.data && error.response.data.message)
                            ? error.response.data.message
                            : 'Something went wrong. Please contact tech support.'

                        context.root.$swal({
                            icon: 'error',
                            title: 'Release Failed',
                            text: message,
                            showConfirmButton: true,
                            customClass: { confirmButton: 'btn btn-danger' },
                            buttonsStyling: false,
                        })
                    })
            })
        }

        return {
            releaseModal, records, releasing,
            selectedSale, selectedMarketId, markets, locationFilter,
            columns, from, to, totalRecords, currentPage, perPage, perPageOptions,
            searchQuery, sortBy, isSortDirDesc,
            fetch, refetch, onLocationFilter, onMarketChange, invokeRelease, confirmRelease,
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

// ─── Tobacco Palette ─────────────────────────────────────
$tb-brown:       #7367f0;   // deep cured-tobacco brown
$tb-dark:        #7367f0;   // dark mahogany
$tb-gold:        #C8900A;   // cured-leaf amber gold
$tb-gold-light:  #D4A832;   // lighter harvest gold
$tb-green:       #3A6B2A;   // tobacco-plant leaf green
$tb-green-light: #4E8A3A;   // lighter leaf green
$tb-cream:       #FDF4E7;   // warm paper cream
$tb-cream-mid:   #F0E4CC;   // mid cream / parchment
$tb-tan:         #C49A6C;   // warm tan
$tb-sand:        #E8D5B0;   // sandy border
$tb-text:        #3A2010;   // body text on cream
$tb-muted:       #8A7560;   // muted tobacco-tone text

// ─── Global helpers ──────────────────────────────────────
.release-tickets-page {
  font-family: inherit;
}

// ─── Filter Card ─────────────────────────────────────────
.filter-card {
  border: 1px solid $tb-sand;
  box-shadow: 0 2px 14px rgba($tb-brown, 0.08);
  border-radius: 0.5rem;

  &__header {
    display: flex;
    align-items: center;
    padding: 0.65rem 1.25rem;
    background: linear-gradient(118deg, $tb-brown, darken($tb-brown, 8%));
    border-radius: 0.42rem 0.42rem 0 0;
    color: $tb-cream;
  }

  &__title {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }
}

.filter-select-wrap {
  .filter-label {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    font-weight: 600;
    color: $tb-brown;
    margin-bottom: 0.3rem;
  }
}

// amber diamond marker for market step
.step-diamond {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: $tb-gold;
  border-radius: 2px;
  transform: rotate(45deg);
  margin-right: 0.5rem;
  flex-shrink: 0;
}

// ─── Sales Card ──────────────────────────────────────────
.sales-card {
  border: 1px solid $tb-sand;
  box-shadow: 0 2px 14px rgba($tb-brown, 0.08);
  border-radius: 0.5rem;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.9rem 1.25rem;
    border-bottom: 1px solid $tb-sand;
    background: $tb-cream;
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1.25rem;
    border-top: 1px solid $tb-sand;
    background: $tb-cream;
    border-radius: 0 0 0.5rem 0.5rem;
  }
}

.sales-header-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, $tb-brown, $tb-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  color: $tb-gold-light;
  box-shadow: 0 4px 12px rgba($tb-brown, 0.35);
  flex-shrink: 0;
}

.sales-title    { color: $tb-brown; }
.sales-subtitle { color: $tb-muted; font-size: 0.78rem; }
.footer-info    { color: $tb-muted; font-size: 0.8rem; white-space: nowrap; }

.record-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba($tb-gold, 0.15);
  color: $tb-gold;
  border: 1px solid rgba($tb-gold, 0.35);
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.1rem 0.5rem;
  vertical-align: middle;
}

// ─── Sales Table ─────────────────────────────────────────
.sales-table {
  ::v-deep {
    thead tr th {
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: $tb-muted;
      background: lighten($tb-cream, 2%);
      border-bottom: 2px solid $tb-sand;
    }

    tbody tr:hover td {
      background: rgba($tb-gold, 0.04);
    }
  }

  .market-cell {
    &__code {
      display: block;
      font-weight: 700;
      font-size: 0.82rem;
      color: $tb-brown;
      line-height: 1.3;
    }
    &__name {
      font-size: 0.72rem;
      color: $tb-muted;
    }
  }

  .sale-number {
    font-weight: 800;
    color: $tb-gold;
    font-size: 0.88rem;
    letter-spacing: 0.02em;
  }

  .tickets-cell {
    min-width: 90px;

    .tickets-captured { font-weight: 700; color: $tb-green; font-size: 0.82rem; }
    .tickets-total    { color: $tb-muted; font-size: 0.82rem; }

    &__bar {
      border-radius: 10px;
      background: $tb-sand;

      ::v-deep .progress-bar { background: $tb-green !important; }
    }
  }

  .action-btn {
    font-size: 0.78rem;
    padding: 0.28rem 0.85rem;
    border-radius: 20px;
    font-weight: 700;
    white-space: nowrap;
    border: 1.5px solid $tb-brown;
    background: transparent;
    color: $tb-brown;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: $tb-brown;
      color: $tb-gold-light;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($tb-brown, 0.4);
    }

    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}

// ─── Status Pills ────────────────────────────────────────
.status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.65rem;
  border-radius: 20px;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 0.35rem;
    flex-shrink: 0;
  }

  &--open {
    background: rgba($tb-green, 0.1);
    color: $tb-green;
    border: 1px solid rgba($tb-green, 0.3);
    .status-dot { background: $tb-green; }
  }

  &--closed {
    background: rgba($tb-muted, 0.1);
    color: $tb-muted;
    border: 1px solid rgba($tb-muted, 0.3);
    .status-dot { background: $tb-muted; }
  }
}

// ─── Empty State ─────────────────────────────────────────
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem 3.5rem;
  text-align: center;

  &__icon {
    position: relative;
    width: 96px;
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: radial-gradient(circle, rgba($tb-gold, 0.12), rgba($tb-cream, 0.6));
    border-radius: 50%;
    border: 1.5px solid rgba($tb-gold, 0.25);
  }

  &__ring {
    position: absolute;
    inset: -10px;
    border-radius: 50%;
    border: 2px dashed rgba($tb-tan, 0.5);
    animation: spin 20s linear infinite;
  }

  &__ico { color: $tb-brown; }

  &__title { color: $tb-brown; }

  &__text {
    color: $tb-muted;
    max-width: 400px;
    font-size: 0.88rem;
    line-height: 1.65;
    strong { color: $tb-brown; }
  }

  &__steps {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.2rem;
    padding: 0.6rem 1.2rem;
    background: $tb-cream;
    border: 1px solid $tb-sand;
    border-radius: 30px;
  }
}

.empty-step {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: $tb-text;
  font-weight: 600;

  &__num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba($tb-brown, 0.1);
    color: $tb-brown;
    font-weight: 800;
    font-size: 0.72rem;
    border: 1.5px solid rgba($tb-brown, 0.2);

    &--gold {
      background: rgba($tb-gold, 0.15);
      color: $tb-gold;
      border-color: rgba($tb-gold, 0.35);
    }
  }

  &__arrow { color: $tb-tan; display: flex; align-items: center; }
}

@keyframes spin { to { transform: rotate(360deg); } }

// ─── Release Modal ───────────────────────────────────────
.rt-modal {
  border-radius: 0.5rem;
  overflow: hidden;

  // Gradient header — dark mahogany to tobacco brown
  &__header {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 1.3rem 1.5rem;
    background: linear-gradient(135deg, $tb-dark 0%, $tb-brown 60%, lighten($tb-brown, 6%) 100%);
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, $tb-gold, $tb-gold-light, $tb-gold);
    }
  }

  &__header-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba($tb-gold, 0.2);
    border: 1.5px solid rgba($tb-gold, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    color: $tb-gold-light;
    flex-shrink: 0;
  }

  &__header-text { flex: 1; }

  &__sub { color: rgba($tb-gold-light, 0.85); font-size: 0.78rem; }

  &__close {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;

    &:hover { background: rgba(255, 255, 255, 0.2); color: #fff; }
  }

  &__body { padding: 1.25rem 1.5rem; }

  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid $tb-sand;
    margin-top: 1rem;
  }
}

// ─── Modal Stat Cards ────────────────────────────────────
.rt-stat {
  background: $tb-cream;
  border: 1px solid $tb-sand;
  border-radius: 10px;
  padding: 0.65rem 0.9rem;
  height: 100%;

  &__label {
    display: block;
    font-size: 0.68rem;
    color: $tb-muted;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  &__value {
    display: block;
    font-size: 0.92rem;
    font-weight: 700;
    color: $tb-text;
  }

  &__value--brown { color: $tb-gold; }
  &__captured { font-weight: 700; color: $tb-green; }
  &__total    { color: $tb-muted; }
}

// ─── Modal Market Row ────────────────────────────────────
.rt-market-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  background: rgba($tb-brown, 0.04);
  border: 1px solid rgba($tb-tan, 0.5);
  border-radius: 10px;
  margin-bottom: 0.75rem;

  &__icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba($tb-gold, 0.15);
    border: 1px solid rgba($tb-gold, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: $tb-gold;
  }

  &__label { font-size: 0.72rem; color: $tb-muted; margin: 0; }
  &__value  { font-weight: 700; color: $tb-brown; font-size: 0.84rem; margin: 0; }
}

// ─── Modal Warning ───────────────────────────────────────
.rt-warning {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  background: rgba($tb-gold, 0.07);
  border-left: 3px solid $tb-gold;
  border-radius: 0 8px 8px 0;
  font-size: 0.82rem;
  color: $tb-text;

  &__icon { color: $tb-gold; }
  strong { color: $tb-brown; }
}

// ─── Release Confirm Button ──────────────────────────────
.release-confirm-btn {
  background: linear-gradient(135deg, $tb-brown, $tb-dark);
  border: none;
  color: $tb-gold-light;
  border-radius: 20px;
  padding: 0.4rem 1.2rem;
  font-weight: 700;
  font-size: 0.82rem;
  box-shadow: 0 4px 14px rgba($tb-brown, 0.45);
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, lighten($tb-brown, 5%), $tb-brown);
    color: $tb-gold-light;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba($tb-brown, 0.55);
  }

  &:disabled { opacity: 0.65; }
}

// ─── Pagination overrides ────────────────────────────────
::v-deep .tobacco-pagination {
  .page-item.active .page-link {
    background: $tb-brown !important;
    border-color: $tb-brown !important;
    box-shadow: 0 2px 8px rgba($tb-brown, 0.4);
  }
  .page-link {
    color: $tb-brown;
    &:hover { background: rgba($tb-brown, 0.08); }
  }
  .page-item.disabled .page-link { color: $tb-muted; }
}

// ─── Dark mode ───────────────────────────────────────────
.dark-layout {
  .filter-card        { border-color: rgba($tb-tan, 0.2); }
  .sales-card         { border-color: rgba($tb-tan, 0.2); }
  .sales-card__header,
  .sales-card__footer { background: #1e1610; border-color: rgba($tb-tan, 0.2); }
  .rt-stat            { background: #28200f; border-color: rgba($tb-tan, 0.2); }
  .rt-market-row      { background: rgba($tb-brown, 0.15); border-color: rgba($tb-tan, 0.2); }
  .empty-state__steps { background: #28200f; border-color: rgba($tb-tan, 0.2); }
  .empty-state__icon  { background: radial-gradient(circle, rgba($tb-gold, 0.15), rgba($tb-dark, 0.6)); }
  .rt-modal__footer   { border-color: rgba($tb-tan, 0.2); }
  .rt-warning         { background: rgba($tb-gold, 0.06); }
}

// ─── Sales card controls (search + per-page) ─────────────
.sales-card__controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sales-search  { width: 180px; }
.sales-perpage { min-width: 75px; }

// ─── Responsive — tablet (< 768px) ───────────────────────
@media (max-width: 767.98px) {

  // Sales card header: stack title + controls vertically
  .sales-card__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.85rem 1rem;
  }

  .sales-card__controls {
    width: 100%;

    .sales-search  { flex: 1; width: auto; }
    .sales-perpage { min-width: 70px; }
  }

  // Footer: stack info above pagination, centred
  .sales-card__footer {
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    padding: 0.75rem 1rem;
    text-align: center;
  }

  // Tighten filter card body
  .filter-card .card-body { padding: 0.85rem 1rem 0.75rem; }

  // Compact empty state
  .empty-state {
    padding: 2.5rem 1.25rem 2rem;
  }
}

// ─── Responsive — mobile (< 576px) ───────────────────────
@media (max-width: 575.98px) {

  // Filter header smaller text
  .filter-card__header { padding: 0.55rem 1rem; }

  // Modal header: tighter padding, smaller icon
  .rt-modal {
    &__header {
      padding: 1rem 1rem 1.1rem;
      gap: 0.65rem;
    }

    &__header-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
    }

    &__header-text h5  { font-size: 1rem; }
    &__sub             { font-size: 0.72rem; }

    &__body { padding: 1rem; }

    // Stack footer buttons, full-width, release first (visual weight on top)
    &__footer {
      flex-direction: column-reverse;
      gap: 0.5rem;

      .btn {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  .release-confirm-btn {
    width: 100%;
    border-radius: 10px;
    padding: 0.55rem 1rem;
  }

  // Stat cards: keep 2-col but tighten padding
  .rt-stat {
    padding: 0.55rem 0.7rem;
    &__label { font-size: 0.64rem; }
    &__value { font-size: 0.85rem; }
  }

  // Market row compact
  .rt-market-row {
    padding: 0.55rem 0.75rem;
    gap: 0.6rem;
  }

  // Warning compact
  .rt-warning { padding: 0.65rem 0.75rem; }

  // Sales card header/footer tight
  .sales-card__header { padding: 0.75rem 0.9rem; }
  .sales-card__footer { padding: 0.65rem 0.9rem; }

  // Controls stack: search on top, per-page below
  .sales-card__controls {
    flex-wrap: wrap;

    .sales-search  { width: 100%; order: 1; }
    .sales-perpage { order: 2; min-width: 80px; }
  }

  // Table: action button icon-only on xs
  ::v-deep .col-action {
    .action-btn {
      padding: 0.3rem 0.6rem;
      font-size: 0;           // hide text label

      svg { margin-right: 0 !important; font-size: 14px; }

      &::after {
        content: '';
        display: none;
      }
    }
  }

  // Empty state compact
  .empty-state {
    padding: 2rem 1rem 1.75rem;

    &__icon  { width: 80px; height: 80px; }
    &__title { font-size: 1rem; }
    &__text  { font-size: 0.8rem; }

    &__steps {
      padding: 0.5rem 0.9rem;
      font-size: 0.78rem;
    }
  }

  .empty-step__num { width: 20px; height: 20px; font-size: 0.68rem; }

  // Pagination: smaller gaps
  ::v-deep .tobacco-pagination .page-link {
    padding: 0.3rem 0.5rem;
    font-size: 0.8rem;
  }
}
</style>
